import { CSSProperties, useCallback, useRef, useState } from 'react';
import { TextField } from '@material-ui/core';

export const TICKET_PATTERN = /^[A-Z]+-[0-9]+$/;

/**
 * 생성·수정·삭제 다이얼로그의 티켓 번호 상태.
 *
 * 한때 티켓이 비면 다음 버튼을 비활성으로만 두었다 — 왜 눌리지 않는지 알 수 없어
 * 헤맸다(특히 수정 다이얼로그는 티켓 칸이 폼 위에 있어 버튼에서 보이지 않는다).
 * 이제 버튼은 티켓 때문에 막지 않고, 누르면 {@link ensureValid} 가 칸을 빨갛게
 * 하고 그리로 옮긴다.
 */
export function useTicket() {
  const [ticket, setTicket] = useState('');
  const [attempted, setAttempted] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const valid = TICKET_PATTERN.test(ticket);

  /** 다음 단계로 가기 전에 부른다. 유효하지 않으면 false 다. */
  const ensureValid = useCallback(() => {
    if (TICKET_PATTERN.test(ticket)) return true;
    setAttempted(true);
    inputRef.current?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
    inputRef.current?.focus();
    return false;
  }, [ticket]);

  const reset = useCallback(() => {
    setTicket('');
    setAttempted(false);
  }, []);

  return { ticket, setTicket, valid, attempted, ensureValid, reset, inputRef };
}

export type TicketState = ReturnType<typeof useTicket>;

export const TicketField = (props: {
  state: TicketState;
  /** 문제가 없을 때의 안내 */
  helperText: string;
  style?: CSSProperties;
}) => {
  const { ticket, setTicket, valid, attempted, inputRef } = props.state;
  const empty = ticket.length === 0;
  // 빈 칸은 진행을 시도한 뒤에만 빨갛게 한다. 열자마자 빨간 칸은 경고가 아니라 소음이다.
  const error = !valid && (!empty || attempted);
  let helperText = props.helperText;
  if (error) {
    helperText = empty
      ? '티켓 번호를 입력해주세요 (예: DPD-123)'
      : '대문자 프로젝트 키와 숫자를 하이픈으로 연결해주세요 (예: DPD-123)';
  }
  return (
    <TextField
      id="ticket-number"
      label="티켓 번호"
      value={ticket}
      onChange={e => setTicket(e.target.value.toUpperCase())}
      required
      fullWidth
      variant="outlined"
      size="small"
      placeholder="DPD-123"
      error={error}
      helperText={helperText}
      inputRef={inputRef}
      style={props.style}
    />
  );
};
